import React, { useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IFolder } from "store/types";

const DIVIDER = "1px solid #E5E8EF";
const INDENT = 32;

interface IProps {
  folder: IFolder;
  subIndex: number;
  selectedFolder: IFolder | null;
  setSelectedFolder: (folder: IFolder | null) => void;
  searchQuery?: string;
}

const folderMatchesSearch = (folder: IFolder, query: string): boolean => {
  if (!query) return true;
  const lower = query.toLowerCase();
  if (folder.title.toLowerCase().includes(lower)) return true;
  return folder.children?.some((child) => folderMatchesSearch(child, lower)) ?? false;
};

export const Folder: React.FC<IProps> = ({ folder, subIndex, selectedFolder, setSelectedFolder, searchQuery = "" }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = Boolean(folder.children?.length);
  const isSelected = selectedFolder?.id === folder.id;
  const isFiltering = Boolean(searchQuery);
  const shouldExpand = expanded || isFiltering;

  const visibleChildren = isFiltering
    ? folder.children?.filter((child) => folderMatchesSearch(child, searchQuery)) ?? []
    : folder.children ?? [];

  if (isFiltering && !folder.title.toLowerCase().includes(searchQuery.toLowerCase()) && visibleChildren.length === 0) {
    return null;
  }

  return (
    <>
      <Stack
        data-folder
        direction="row"
        alignItems="center"
        sx={{
          borderBottom: DIVIDER,
          cursor: "pointer",
          userSelect: "none",
          height: 32,
          backgroundColor: isSelected ? "#EDF4FB" : "transparent",
          "&:hover": { backgroundColor: isSelected ? "#EDF4FB" : "#F5F7FA" }
        }}
        onClick={() => setSelectedFolder(selectedFolder?.id === folder.id ? null : folder)}
      >
        <Box sx={{ width: INDENT + subIndex * INDENT, flexShrink: 0 }} />
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
          <ExpandMoreIcon
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) setExpanded(!expanded);
            }}
            sx={{
              fontSize: 20,
              color: "#071F35",
              transition: "transform 0.15s",
              transform: shouldExpand ? "none" : "rotate(-90deg)",
              visibility: hasChildren ? "visible" : "hidden",
              cursor: hasChildren ? "pointer" : "default"
            }}
          />
          <FolderOutlinedIcon sx={{ fontSize: 16, color: "#071F35" }} />
          <MaterialTooltip variant="dark" title={folder.title} placement="top" isShow={folder.title.length <= 35}>
            <Typography noWrap sx={{ fontSize: 14, color: "#071F35" }}>
              {folder.title}
            </Typography>
          </MaterialTooltip>
        </Box>
      </Stack>
      {shouldExpand &&
        visibleChildren.map((subFolder) => (
          <Folder
            key={subFolder.id}
            folder={subFolder}
            subIndex={subIndex + 1}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            searchQuery={searchQuery}
          />
        ))}
    </>
  );
};
